import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const MaterialesClase = () => {
  const { claseId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [materiales, setMateriales] = useState([]);
  const [clase, setClase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('link'); // Solo Google Drive Link — la carga de archivos se deshabilitó por storage ephemeral
  const [newMaterial, setNewMaterial] = useState({
    descripcion: '',
    archivo: null,
    googleDriveLink: ''
  });

  const MAX_MATERIALES = 10;

  useEffect(() => {
    if (claseId) {
      loadData();
    } else {
      navigate('/app/clases');
    }
  }, [claseId]);

  const loadData = async () => {
    try {
      const [materialesRes, clasesRes] = await Promise.all([
        axios.get(`${API}/clases/${claseId}/materiales`),
        axios.get(`${API}/clases`)
      ]);
      
      setMateriales(materialesRes.data);
      
      const claseEncontrada = clasesRes.data.find(c => c.id === claseId);
      setClase(claseEncontrada);
    } catch (error) {
      toast.error('Error al cargar materiales');
      navigate('/app/clases');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 50MB for presentations and PDFs)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('El archivo debe ser menor a 50MB');
        return;
      }
      
      // Validate file type - Only PDF and PowerPoint
      const allowedTypes = ['pdf', 'pptx', 'ppt'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        toast.error('Solo se permiten archivos PDF (.pdf) y PowerPoint (.ppt, .pptx)');
        return;
      }
      
      setNewMaterial({ ...newMaterial, archivo: file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Check if limit reached
    if (materiales.length >= MAX_MATERIALES) {
      toast.error(`No se pueden subir más de ${MAX_MATERIALES} materiales por clase`);
      return;
    }
    
    // Validar según el modo
    if (uploadMode === 'file' && !newMaterial.archivo) {
      toast.error('Por favor selecciona un archivo');
      return;
    }
    
    if (uploadMode === 'link' && !newMaterial.googleDriveLink) {
      toast.error('Por favor ingresa el link de Google Drive');
      return;
    }

    setUploading(true);
    try {
      if (uploadMode === 'file') {
        // Upload de archivo tradicional
        const formData = new FormData();
        formData.append('file', newMaterial.archivo);
        if (newMaterial.descripcion) {
          formData.append('descripcion', newMaterial.descripcion);
        }

        await axios.post(`${API}/clases/${claseId}/materiales`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
      } else {
        // Upload de link de Google Drive
        await axios.post(`${API}/clases/${claseId}/materiales/link`, {
          descripcion: newMaterial.descripcion || 'Material de Google Drive',
          google_drive_link: newMaterial.googleDriveLink
        });
      }

      setNewMaterial({ descripcion: '', archivo: null, googleDriveLink: '' });
      setIsDialogOpen(false);
      toast.success('Material agregado exitosamente');
      loadData();
    } catch (error) {
      console.error('Error uploading material:', error);
      
      // Handle different error formats
      let errorMessage = 'Error al subir material';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Check if detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err).join(', ');
        } 
        // Check if detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Check if detail is an object
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este material?')) {
      return;
    }

    try {
      await axios.delete(`${API}/materiales/${materialId}`);
      toast.success('Material eliminado exitosamente');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar material');
    }
  };

  const handleDownload = async (materialId, nombreArchivo) => {
    try {
      const response = await axios.get(`${API}/materiales/${materialId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al descargar material');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (tipo) => {
    const icons = {
      'pdf': 'fas fa-file-pdf text-red-600',
      'pptx': 'fas fa-file-powerpoint text-orange-600',
      'ppt': 'fas fa-file-powerpoint text-orange-600',
      'google_drive': 'fab fa-google-drive text-green-600',
    };
    return icons[tipo.toLowerCase()] || 'fas fa-file text-gray-600';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!clase) {
    return (
      <div className="space-y-8">
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-exclamation-triangle text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Clase no encontrada</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="materiales-clase-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/clases')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver a Clases
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Materiales de Clase
          </h1>
          <p className="text-gray-600">
            Clase: <span className="font-medium">{clase.titulo}</span>
          </p>
        </div>
        
        {(user.rol === 'admin' || (user.rol === 'profesor' && clase.profesor_id === user.id)) && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="mt-4 lg:mt-0 instituto-gradient btn-hover text-white" 
                data-testid="upload-material-button"
                disabled={materiales.length >= MAX_MATERIALES}
              >
                <i className="fab fa-google-drive mr-2"></i>
                Compartir Material ({materiales.length}/{MAX_MATERIALES})
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Compartir Material de Clase (Google Drive)</DialogTitle>
              </DialogHeader>
              
              {/* Warning if limit reached */}
              {materiales.length >= MAX_MATERIALES && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-exclamation-triangle text-red-600 mt-1"></i>
                    <div className="text-sm text-red-800">
                      Has alcanzado el límite de {MAX_MATERIALES} materiales por clase. Elimina uno existente para subir uno nuevo.
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleUpload} className="space-y-4">
                {/* Solo modo Google Drive — la carga de archivos locales fue removida
                    porque el storage del deploy es ephemeral y los PDFs se perdían tras cada redeploy.
                    Los profesores deben compartir un link de Google Drive. */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <i className="fab fa-google-drive text-green-600 text-xl mt-1"></i>
                    <div className="text-sm text-green-900">
                      <p className="font-semibold mb-1">Cómo compartir tu material:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Subí tu PDF/PPTX a <strong>tu Google Drive</strong></li>
                        <li>Compartilo como <strong>&quot;Cualquiera con el enlace puede ver&quot;</strong></li>
                        <li>Copiá el link y pegalo abajo</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {uploadMode === 'file' ? (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-1">Especificaciones:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Máximo {MAX_MATERIALES} archivos por clase</li>
                            <li>Solo archivos PDF (.pdf) y PowerPoint (.ppt, .pptx)</li>
                            <li>Tamaño máximo: 50MB por archivo</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="archivo">Archivo *</Label>
                      <input
                        id="archivo"
                        type="file"
                        accept=".pdf,.pptx,.ppt"
                        onChange={handleFileChange}
                        className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        data-testid="material-archivo-input"
                      />
                      {newMaterial.archivo && (
                        <div className="text-sm text-green-600 flex items-center">
                          <i className="fas fa-check-circle mr-2"></i>
                          Archivo seleccionado: {newMaterial.archivo.name}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <i className="fab fa-google-drive text-green-600 mt-1 text-xl"></i>
                        <div className="text-sm text-green-800">
                          <p className="font-semibold mb-1">Instrucciones:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Abre el archivo en Google Drive</li>
                            <li>Haz clic derecho → Compartir → Copiar enlace</li>
                            <li>Asegúrate de que el enlace sea accesible para "Cualquier persona con el enlace"</li>
                            <li>Pega el enlace aquí abajo</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="googleDriveLink">Link de Google Drive *</Label>
                      <input
                        id="googleDriveLink"
                        type="url"
                        value={newMaterial.googleDriveLink}
                        onChange={(e) => setNewMaterial({ ...newMaterial, googleDriveLink: e.target.value })}
                        placeholder="https://drive.google.com/file/d/..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción {uploadMode === 'file' ? '(opcional)' : '*'}</Label>
                  <Textarea
                    id="descripcion"
                    value={newMaterial.descripcion}
                    onChange={(e) => setNewMaterial({ ...newMaterial, descripcion: e.target.value })}
                    placeholder={uploadMode === 'file' ? "Ej: Libro de Génesis - Capítulos 1-12" : "Ej: Presentación de Hermenéutica - Unidad 3"}
                    rows={3}
                    data-testid="material-descripcion-input"
                    required={uploadMode === 'link'}
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      setNewMaterial({ descripcion: '', archivo: null });
                    }}
                    disabled={uploading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="instituto-gradient text-white"
                    disabled={uploading || materiales.length >= MAX_MATERIALES}
                    data-testid="material-submit-button"
                  >
                    {uploading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <i className="fab fa-google-drive mr-2"></i>
                        Compartir Link
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Materials List */}
      {materiales.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {materiales.map((material) => (
            <Card key={material.id} className="card-hover shadow-lg border-0 overflow-hidden">
              <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-teal-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="text-2xl">
                      <i className={getFileIcon(material.tipo_archivo)}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg text-gray-900 truncate">
                        {material.nombre_archivo}
                      </CardTitle>
                      <p className="text-sm text-green-600 font-medium">
                        {material.tipo_archivo === 'google_drive' ? 'GOOGLE DRIVE' : material.tipo_archivo.toUpperCase()}
                        {material.tipo_archivo !== 'google_drive' && ` • ${formatFileSize(material.tamaño)}`}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                {material.descripcion && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {material.descripcion}
                  </p>
                )}
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-user mr-2 text-blue-600"></i>
                    <span>Subido por: {material.subido_por_nombre}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-calendar mr-2 text-green-600"></i>
                    <span>{formatDate(material.fecha_subida)}</span>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 btn-hover"
                    onClick={() => {
                      if (material.tipo_archivo === 'google_drive') {
                        window.open(material.archivo_url, '_blank');
                      } else {
                        // /uploads/xxx.pdf → /api/files/xxx.pdf (el backend sirve archivos en /api/files/)
                        const raw = material.archivo_url || '';
                        const filename = raw.replace(/^\/api\/files\//, '').replace(/^\/uploads\//, '').replace(/^\/api\/uploads\//, '');
                        const backendBase = API.replace('/api', '');
                        const finalUrl = raw.startsWith('http')
                          ? raw
                          : `${backendBase}/api/files/${filename}`;
                        window.open(finalUrl, '_blank');
                      }
                    }}
                    data-testid={`download-material-${material.id}`}
                  >
                    {material.tipo_archivo === 'google_drive' ? (
                      <>
                        <i className="fab fa-google-drive mr-2 text-green-600"></i>
                        Abrir en Drive
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download mr-2 text-blue-600"></i>
                        Descargar
                      </>
                    )}
                  </Button>
                  
                  {(user.rol === 'admin' || material.subido_por === user.id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(material.id)}
                      data-testid={`delete-material-${material.id}`}
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-folder-open text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay materiales compartidos</h3>
            <p className="text-gray-600 mb-6">
              {['estudiante', 'pastor', 'graduado'].includes(user.rol)
                ? 'El profesor aún no ha compartido materiales para esta clase.'
                : 'Comienza subiendo el primer material para compartir con tus estudiantes.'}
            </p>
            {(user.rol === 'admin' || (user.rol === 'profesor' && clase.profesor_id === user.id)) && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="instituto-gradient text-white btn-hover"
              >
                <i className="fas fa-upload mr-2"></i>
                Subir primer material
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="shadow-md border-0 border-l-4 border-l-blue-500">
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <i className="fas fa-info-circle text-blue-600 text-xl mt-1"></i>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-3 text-lg">Información sobre Materiales de Clase</h4>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                    <i className="fas fa-file-upload text-green-600 mr-2"></i>
                    Para Profesores
                  </h5>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <i className="fas fa-check text-green-600 mr-2 mt-0.5"></i>
                      <span>Máximo <strong>{MAX_MATERIALES} archivos</strong> por clase</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check text-green-600 mr-2 mt-0.5"></i>
                      <span>Solo <strong>PDF</strong> y <strong>PowerPoint</strong> (.ppt, .pptx)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check text-green-600 mr-2 mt-0.5"></i>
                      <span>Tamaño máximo: <strong>50MB</strong> por archivo</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check text-green-600 mr-2 mt-0.5"></i>
                      <span>Puedes eliminar materiales para subir nuevos</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                    <i className="fas fa-graduation-cap text-blue-600 mr-2"></i>
                    Para Estudiantes
                  </h5>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <i className="fas fa-eye text-blue-600 mr-2 mt-0.5"></i>
                      <span>Todos los materiales son visibles para estudiantes inscritos</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-download text-blue-600 mr-2 mt-0.5"></i>
                      <span>Puedes descargar libros digitales y presentaciones</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-book-open text-blue-600 mr-2 mt-0.5"></i>
                      <span>Utiliza estos materiales para complementar tus estudios</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <i className="fas fa-lightbulb text-yellow-600 mr-2 mt-0.5"></i>
                  <span><strong>Tip:</strong> Usa nombres descriptivos para tus archivos, ej: "Genesis_Capitulos_1-12.pdf"</span>
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MaterialesClase;
